/**
 * A lista de Casos da equipe por pasta (owner, 04/11), como a do portal (1c7245ab): os casos
 * agrupados pelo processo da pasta (o SBR do cliente). Cada CCB é um caso (ADR-0098), e a pasta
 * junta as CCBs do mesmo processo numa linha só.
 *
 * A pasta do caso é o espelho do processo em `title` (`ProcessosRepository.espelhar_nos_exames`);
 * sem pasta, o título é o próprio código do caso. Esses casos, e os sem processo, ficam juntos em
 * "Sem pasta informada", como no portal. A ordem das pastas é a da lista (`ordemDaLista`): o caso
 * mais urgente da pasta a puxa para cima (prioritário, depois o prazo), e o "Sem pasta" também,
 * para o caso urgente sem pasta não ir para o fim. Puro: a tela só desenha.
 */
import type { CaseStatus as UIStatus } from '@/components/ui/StatusPill';
import type { CaseRecordData } from '@/lib/cases';

import { prazoQueConta } from './ordemDaLista';
import { situacaoNaLista } from './url_state';

export const SEM_PASTA = 'sem-pasta';

export interface SituacaoNaPasta {
  status: UIStatus;
  label: string;
  n: number;
}

export interface PastaDaEquipe {
  /** O processo da pasta, ou `SEM_PASTA`. */
  chave: string;
  pasta: string | null;
  processos: string[];
  titulares: string[];
  casos: CaseRecordData[];
  /** As CCBs distintas dos casos da pasta (pelo número do contrato). */
  ccbs: number;
  prioritaria: boolean;
  /** O caso da pasta com o prazo mais próximo que ainda conta, ou `null`. */
  casoDoPrazo: CaseRecordData | null;
  /** Quantos casos em cada situação, na ordem em que aparecem. */
  situacoes: SituacaoNaPasta[];
}

function pastaDoCaso(caso: CaseRecordData): string | null {
  const titulo = caso.title.trim();
  if (!caso.processo_id || titulo === '' || titulo === caso.display_code) return null;
  return titulo;
}

function chaveDaPasta(caso: CaseRecordData): string {
  return pastaDoCaso(caso) === null ? SEM_PASTA : (caso.processo_id ?? SEM_PASTA);
}

function distintos(valores: (string | null | undefined)[]): string[] {
  return [...new Set(valores.filter((v): v is string => typeof v === 'string' && v.trim() !== ''))];
}

function situacoesDaPasta(
  casos: readonly CaseRecordData[],
  emPreparacao: ReadonlySet<string>,
): SituacaoNaPasta[] {
  const porRotulo = new Map<string, SituacaoNaPasta>();
  for (const caso of casos) {
    const { status, label } = situacaoNaLista(caso, emPreparacao.has(caso.id));
    const atual = porRotulo.get(label);
    porRotulo.set(label, { status, label, n: (atual?.n ?? 0) + 1 });
  }
  return [...porRotulo.values()];
}

function casoDoPrazo(casos: readonly CaseRecordData[]): CaseRecordData | null {
  let escolhido: CaseRecordData | null = null;
  for (const caso of casos) {
    const prazo = prazoQueConta(caso);
    if (prazo === null) continue;
    const atual = escolhido === null ? null : prazoQueConta(escolhido);
    if (atual === null || prazo < atual) escolhido = caso;
  }
  return escolhido;
}

function daPasta(
  chave: string,
  casos: CaseRecordData[],
  emPreparacao: ReadonlySet<string>,
): PastaDaEquipe {
  const primeiro = casos[0];
  return {
    chave,
    pasta: chave === SEM_PASTA || primeiro === undefined ? null : pastaDoCaso(primeiro),
    processos: distintos(casos.map((c) => c.process_number)),
    titulares: distintos(casos.map((c) => c.parties)),
    casos,
    ccbs: distintos(casos.map((c) => c.contrato)).length,
    prioritaria: casos.some((c) => c.prioritario === true),
    casoDoPrazo: casoDoPrazo(casos),
    situacoes: situacoesDaPasta(casos, emPreparacao),
  };
}

/** As pastas dos casos já na ordem da lista; a pasta entra onde aparece o primeiro caso dela. */
export function pastasDaEquipe(
  casos: readonly CaseRecordData[],
  emPreparacao: ReadonlySet<string> = new Set(),
): PastaDaEquipe[] {
  const porChave = new Map<string, CaseRecordData[]>();
  for (const caso of casos) {
    const chave = chaveDaPasta(caso);
    porChave.set(chave, [...(porChave.get(chave) ?? []), caso]);
  }
  return [...porChave.entries()].map(([chave, doGrupo]) => daPasta(chave, doGrupo, emPreparacao));
}
